import { Button, Modal, Typography } from "antd";
import React from "react";
import { ATSApp } from "../../../Interface";
import { openNotification } from "../../../Components/Notification";

interface AppCardProps {
  app: ATSApp;
  handleConnectClick: (app: ATSApp) => Promise<void>;
  isConnected: boolean;
  handleDisconnectClick: (
    appType: "zoho" | "lever" | "custom"
  ) => Promise<void>;
  handleGenerateAPIKeyClick: (
    appType: "zoho" | "lever" | "custom"
  ) => Promise<void>;
  apiKey: string | null;
}

const AppCard: React.FC<AppCardProps> = ({
  app,
  handleConnectClick,
  isConnected = false,
  handleDisconnectClick,
  handleGenerateAPIKeyClick,
  apiKey,
}) => {
  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        openNotification("success", "API Key copied to clipboard", "");
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          if (successful) {
            openNotification("success", "API Key copied to clipboard", "");
          } else {
            throw new Error("Copy command failed");
          }
        } catch (err) {
          throw new Error("Copy command failed");
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      openNotification(
        "error",
        "Failed to copy API Key",
        "Please copy the key manually from the input field"
      );
    }
  };

  const handleButtonClick = async (
    action: "copy" | "generate" | "disconnect" | "connect"
  ) => {
    if (action === "copy") {
      return await copyToClipboard(apiKey || "");
    } else if (action === "generate") {
      return await handleGenerateAPIKeyClick(app.type);
    } else if (action === "disconnect") {
      Modal.confirm({
        centered: true,
        maskClosable: false,
        icon: null,
        title: "Disconnect",
        content: `Are you sure you want to disconnect this ${app.name}?`,
        onOk: async () => {
          return await handleDisconnectClick(app.type);
        },
        onCancel: () => {
          openNotification("info", "Disconnection cancelled", "");
          return;
        },
      });
    } else if (action === "connect") {
      return await handleConnectClick(app);
    }
  };

  return (
    <div className="app-card">
      <div className="app-card-header">
        <div className="app-card-header-icon">
          <img src={app.icon} alt={app.name} />
        </div>
        <div className="app-card-header-cta">
          <Button
            type={isConnected ? "default" : "primary"}
            size="small"
            shape="round"
            onClick={() =>
              handleButtonClick(isConnected ? "disconnect" : "connect")
            }
          >
            {isConnected ? "Disconnect" : "Connect"}
          </Button>
        </div>
      </div>
      <div className="app-card-title">
        <Typography.Title level={5} style={{ margin: 0 }}>
          {app.name}
        </Typography.Title>
        {isConnected && (
          <Typography.Text type="success" style={{ fontSize: "12px" }}>
            • Connected
          </Typography.Text>
        )}
      </div>
      <div className="app-card-description">
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          {app.description}
        </Typography.Paragraph>
      </div>
      {/* 
      {isConnected && (
        <div className="app-card-footer">
          <Typography.Title level={5} style={{ margin: 0 }}>
            API Key
          </Typography.Title>
          <Space.Compact style={{ width: "100%" }}>
            <Input value={apiKey || "Generate API Key"} readOnly />
            <Button
              type="primary"
              onClick={() => handleButtonClick(apiKey ? "copy" : "generate")}
            >
              {apiKey ? "Copy" : "Generate"}
            </Button>
          </Space.Compact>
        </div>
      )} */}
    </div>
  );
};

export default AppCard;
